<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">新增取貨預約</h1>
      </div>
      <div class="header-actions">
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="submitting" @click="handleSubmit">
          <span v-if="submitting">建立中...</span>
          <span v-else>建立取貨預約</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-pickup-appointment-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">取貨資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="storeId" class="required">門市</label>
              <select
                id="storeId"
                v-model="form.storeId"
                class="form-control"
                :class="{ 'is-invalid': errors.storeId }"
                required
                @change="handleStoreChange"
              >
                <option value="">請選擇門市</option>
                <option v-for="store in stores" :key="store.id" :value="store.id">
                  {{ store.storeName }}
                </option>
              </select>
              <div v-if="errors.storeId" class="invalid-feedback">
                {{ errors.storeId }}
              </div>
            </div>

            <div class="form-group">
              <label for="pickupDate" class="required">取貨日期</label>
              <input
                id="pickupDate"
                v-model="form.pickupDate"
                type="date"
                class="form-control"
                :class="{ 'is-invalid': errors.pickupDate }"
                :min="minDate"
                required
                @change="handleDateChange"
              />
              <div v-if="errors.pickupDate" class="invalid-feedback">
                {{ errors.pickupDate }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="pickupTimeSlot" class="required">取貨時段</label>
              <select
                id="pickupTimeSlot"
                v-model="form.pickupTimeSlot"
                class="form-control"
                :class="{ 'is-invalid': errors.pickupTimeSlot }"
                :disabled="!form.pickupDate || !form.storeId"
                required
              >
                <option value="">請選擇時段</option>
                <option
                  v-for="slot in availableSlots"
                  :key="slot.timeSlot"
                  :value="slot.timeSlot"
                  :disabled="!slot.isAvailable"
                >
                  {{ slot.timeSlot }} {{ slot.isAvailable ? '' : '(已額滿)' }}
                </option>
              </select>
              <div v-if="errors.pickupTimeSlot" class="invalid-feedback">
                {{ errors.pickupTimeSlot }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="orderId">訂單編號</label>
              <input
                id="orderId"
                v-model="form.orderId"
                type="text"
                class="form-control"
                placeholder="選填：關聯的訂單編號"
              />
            </div>

            <div class="form-group">
              <label for="memberId">會員</label>
              <input
                id="memberId"
                v-model="form.memberId"
                type="text"
                class="form-control"
                placeholder="選填：會員 ID"
              />
            </div>
          </div>
        </div>

      </form>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { pickupAppointmentService } from '@/api/modules/pickupAppointments.js';
import { storeService } from '@/api/modules/stores.js';
import { showSuccess, showError } from '@/utils/swal.js';

export default {
  name: 'CreatePickupAppointment',
  setup() {
    const router = useRouter();
    const stores = ref([]);
    const availableSlots = ref([]);
    const submitting = ref(false);
    const errors = ref({});

    const form = ref({
      storeId: '',
      pickupDate: '',
      pickupTimeSlot: '',
      orderId: '',
      memberId: ''
    });

    const minDate = computed(() => {
      return new Date().toISOString().split('T')[0];
    });

    const fetchStores = async () => {
      try {
        const response = await storeService.getStores({ page: 1, pageSize: 100 });
        if (response.success && response.data?.items) {
          stores.value = response.data.items;
          // 預設選擇第一家門市
          if (!form.value.storeId && stores.value.length > 0) {
            form.value.storeId = stores.value[0].id;
            // 預設日期為今天
            form.value.pickupDate = minDate.value;
            await fetchAvailableSlots();
          }
        }
      } catch (err) {
        console.error('Failed to fetch stores:', err);
      }
    };

    const fetchAvailableSlots = async () => {
      if (!form.value.storeId || !form.value.pickupDate) {
        availableSlots.value = [];
        return;
      }

      try {
        const response = await pickupAppointmentService.getAvailableSlots(
          form.value.storeId,
          form.value.pickupDate
        );
        if (response.success && response.data?.availableSlots) {
          availableSlots.value = response.data.availableSlots;
        }
      } catch (err) {
        console.error('Failed to fetch available slots:', err);
        availableSlots.value = [];
      }
    };

    const handleStoreChange = () => {
      form.value.pickupTimeSlot = '';
      if (form.value.pickupDate) {
        fetchAvailableSlots();
      }
    };

    const handleDateChange = () => {
      form.value.pickupTimeSlot = '';
      if (form.value.storeId) {
        fetchAvailableSlots();
      }
    };

    const handleSubmit = async () => {
      errors.value = {};
      submitting.value = true;

      try {
        // 移除空字串的選填欄位，避免 UUID 驗證失敗
        const payload = { ...form.value };
        if (!payload.orderId) delete payload.orderId;
        if (!payload.memberId) delete payload.memberId;

        await pickupAppointmentService.createPickupAppointment(payload);
        showSuccess('取貨預約建立成功');
        router.push('/client/pickup-appointments');
      } catch (err) {
        const errorData = err.response?.data;
        if (errorData?.error?.details) {
          errors.value = errorData.error.details;
        }
        showError(errorData?.message || '建立取貨預約失敗');
      } finally {
        submitting.value = false;
      }
    };

    const handleCancel = () => {
      router.push('/client/pickup-appointments');
    };

    onMounted(() => {
      fetchStores();
    });

    return {
      form,
      stores,
      availableSlots,
      submitting,
      errors,
      minDate,
      handleStoreChange,
      handleDateChange,
      handleSubmit,
      handleCancel
    };
  }
};
</script>

<style scoped>
.create-pickup-appointment-container {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 2px solid #e0e0e0;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.form-group {
  display: flex;
  flex-direction: column;
}

.form-group.full-width {
  grid-column: 1 / -1;
}

.form-group label {
  margin-bottom: 6px;
  font-weight: 500;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #e0e0e0;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
